import { BulletPool, type Bounds } from './bullets/BulletPool';
import { KIND_GEM, KIND_SPARK } from './bullets/bulletKinds';
import {
  AbilitySystem, RIME_AMP, RIME_SHATTER_FRAC,
} from './abilities/AbilitySystem';
import {
  BRAND_AMP, SHRED_PER_STACK, skillOnEnemyHit, skillOnKill, skillOnPlayerHit,
  type Corpse,
} from './abilities/skillHooks';
import { SHOT_KINDS, shotBehavior, spawnPlayerShot } from './abilities/shots';
import {
  CASK_BURN_FLOOR, CASK_SAFE_RIM,
  HOUND_HP, HOUND_VACUUM_RANGE, MOURNE_FLOOR_RESERVE, aspectHealBlock,
  aspectLethalSave, aspectOnKill, relightCandle, tickAspects,
} from './characters/aspects';
import { tickActives } from './characters/actives';
import { buildPatternRegistry } from './bullets/patterns/library';
import { PhaseRunner, type PatternContext } from './bullets/patterns/PatternEmitter';
import { toSteps } from './bullets/patterns/schema';
import { TelegraphSystem } from './combat/TelegraphSystem';
import { SpatialHash } from './combat/SpatialHash';
import { Enemy } from './entities/Enemy';
import { updateEnemy } from './entities/behaviors';
import {
  ENEMY_DEFS, GOBLIN_CHANCE, GOBLIN_MIN_FLOOR, GOBLIN_PANIC_SECONDS,
  enemyHpMult, goblinSecondDropChance,
} from './entities/enemyDefs';
import type { ArtifactProc } from './artifacts/ArtifactDefs';
import type { Player } from './entities/Player';
import { HARD_ENEMY_CAP, buildSchedule, SpawnDirector, type SpawnEvent } from './director/SpawnDirector';
import { rollFloorEvents } from './events/floorEvents';
import type {
  AmbushEvent, BoneKeyEvent, BrazierRelayEvent, ChestEvent,
  FloorEvent, SpringEvent, StrongboxEvent,
} from './events/EventDefs';
import type { EliteAffix } from './entities/enemyDefs';
import {
  ELITE_FILL, KILL_GEM_NORM, REGULAR_FILL, RUN_TIME_LIMIT_SECONDS, gemMultiplier, killFillMult, summonBarFill,
} from './run/progression';
import { PICKUP_BUMP_CAP, PICKUP_BUMP_TABLE, PICKUP_HEAL_CHANCE, pickupBumpsEarned } from './run/pickupTrack';
import { ENRAGE_SPAWN_CAP, ENRAGE_TYPES, enragePackSize, enrageSpawnInterval, enrageSpeedMult } from './run/enrage';
import { rollArtifactDrop, rollArtifactProc } from './artifacts/ArtifactSystem';
import { rollQuality } from './meta/economy';
import { scaleTierDamage, tierHpMult, tierQualityFloor } from './run/tiers';
import { BOSS_DEFS, bossGemMult, bossHpMult, bossTracksForFloor, type BossDef } from './boss/BossDefs';
import type { RunState } from './run/RunState';
import type { GeneratedMap } from '../map/MapGen';
import { TILE, bfsDistances, nearestFloor } from '../map/MapGen';
import type { TileMap } from '../map/TileMap';
import type { EventBus } from '../core/EventBus';
import type { InputSnapshot } from '../input/actions';
import { gameplayRng } from '../core/rng/RngStreams';
import {
  DEBRIS_DAMAGE, DEBRIS_ENEMY_DAMAGE, DEBRIS_LEAD_STEPS, DEBRIS_PERIOD,
  DEBRIS_RADIUS, DEBRIS_STEPS, DEBRIS_STEP_DELAY,
  GAS_DPS, GAS_DURATION, VENT_DAMAGE, VENT_ENEMY_DAMAGE, VENT_LEAD_STEPS,
  VENT_PERIOD, VENT_RADIUS, rollHazards, type Hazard,
} from './hazards';
import { TAU, dist2, segPointDist2 } from '../core/math';

const GEM_COLLECT_RANGE = 24;
const CONTACT_CHECK_RANGE = 64;
/** Gem-hoard chest base value (floor-scaled by gemMultiplier at open). */
const CHEST_HOARD_BASE = 90;
/** Damage-number aggregation window (seconds) — dots merge into one number. */
const DMG_POPUP_WINDOW = 0.3;
/** Geodes (ME3): shatter payout base (×gemMultiplier) and hit radius. */
const GEODE_GEMS_BASE = 12;
const GEODE_RADIUS = 14;

/**
 * Per-floor simulation container. Pixi-free — headless tests run the whole
 * thing. RunScene owns rendering and subscribes to `events` for feedback.
 */
export class World {
  enemies: Enemy[] = [];
  readonly enemyBullets = new BulletPool(4096);
  readonly playerShots = new BulletPool(512);
  readonly particles = new BulletPool(2048);
  readonly gems = new BulletPool(1200);
  readonly telegraphs = new TelegraphSystem();
  readonly hash = new SpatialHash();
  readonly director: SpawnDirector;
  readonly patternCtx: PatternContext;
  readonly bounds: Bounds;
  readonly abilitySystem = new AbilitySystem();
  /** Artifact drops waiting on the ground (suppressed = swap declined nearby). */
  readonly artifactDrops: {
    x: number; y: number; artifactId: string; quality: number;
    proc: ArtifactProc; curseId: string | null; suppressed: boolean;
  }[] = [];
  /** Seed-placed floor events, pre-rolled in the ctor (kill-order-proof). */
  readonly floorEvents: FloorEvent[];
  /**
   * Ground zones (S1): lingering areas from skills/aspects (Caustic pools,
   * Sanctum, Wick's burning trail). Spawned at corpse/live positions — rng-
   * free by construction. kind 'burn' applies a burn DoT instead of direct
   * dps; slowPct > 0 slows enemies inside (via zoneSlow fields).
   */
  /** 'gas' (ME4 vents) is the ONE hostile zone kind — the player takes
   *  contact damage inside it; every other kind stays player-benign. */
  readonly zones: {
    x: number; y: number; radius: number; duration: number;
    dps: number; slowPct: number; kind: 'caustic' | 'sanctum' | 'burn' | 'gas';
  }[] = [];
  /** Environmental hazards (ME4): biome-gated, floors ≥2, never in the vault. */
  readonly hazards: Hazard[] = [];
  /**
   * Deaths this tick (S3), drained iteratively after collisions so on-kill
   * chains (Rupture) resolve in-tick without recursion.
   */
  readonly pendingCorpses: Corpse[] = [];
  /** True while the corpse queue is draining — marks chain-caused deaths. */
  private drainingCorpses = false;

  // ---- Aspect state (per-floor transients; aspects.ts owns the rules) ----
  /** Mourne's crawlers: melee-only, untargetable, gameplayRng-jitter only. */
  readonly crawlers: { x: number; y: number; damage: number; ttl: number; biteTick: number }[] = [];
  /** Exhume enrage window (seconds): swarm ×1.5 speed/damage, specials first. */
  crawlerEnrage = 0;
  /** Bram & Gaunt's hound (null unless playing them). */
  hound: {
    x: number; y: number; hp: number; maxHp: number; alive: boolean;
    state: 'follow' | 'dash'; stateLeft: number; dashVx: number; dashVy: number;
    charge: number; chipTick: number; biteTick: number; dashHit: Set<number>;
  } | null = null;
  /** Barrow's Ironbound: taken hits → decaying aura stacks. */
  ironboundStacks = 0;
  ironboundFade = 0;
  ironboundTick = 0;
  /** Trine's Sunder window/cooldown. */
  sunderLeft = 0;
  sunderCd = 8;
  /** Wick's guttered-candle chip accumulator. */
  candleChipAcc = 0;
  /** Cask's burn-cooling accumulator (aspects.ts; per-floor like the rest). */
  caskCoolAcc = 0;
  /** Thirl's Ebb: seconds since the leech chain last connected — past
   *  EBB_QUIET_SECONDS the drain runs ×EBB_QUIET_MULT (aspects.ts). */
  leechQuiet = 0;
  /** Brand: seconds since a kill last fed the marks — past
   *  BRAND_QUIET_SECONDS the next hit brands directly (skillHooks.ts). */
  brandQuiet = 0;
  /** Active ability cooldown (actives.ts; per-floor like the rest). */
  activeCd = 0;
  /** Thirl's Bloodprice: the next leech chain fires empowered (one-shot). */
  bloodprice = false;
  /** Mourne's ambient-birth accumulator (per-floor; a fresh floor restarts it). */
  ambientBirthAcc = 0;

  // ---- Damage numbers (2026-07-27, Sarah's ask)
  /**
   * Floating-number feed the renderer DRAINS each frame. Enemy damage is
   * AGGREGATED per enemy over DMG_POPUP_WINDOW (dot ticks at 60Hz would
   * spam thousands); killing blows flush immediately. 'taken' = player was
   * hit, 'burn' = Cask scorch. Capped so headless runs can't grow it.
   */
  readonly damagePopups: {
    x: number; y: number; amount: number; kind: 'hit' | 'dot' | 'taken' | 'burn';
  }[] = [];
  private readonly dmgAcc = new Map<number, {
    x: number; y: number; sum: number; ttl: number; hasHit: boolean;
  }>();

  // ---- Findables (ME3)
  /** Bone key (strongbox pair) — per-floor state, dies with the World. */
  keyHeld = false;
  /** Shatterable gem geodes: `scatter` stream, never in the vault. */
  readonly geodes: { x: number; y: number; broken: boolean }[] = [];

  // ---- Summoning & boss state
  circle: { x: number; y: number } | null = null;
  /** Channel progress in seconds (1.5 to trigger). */
  channel = 0;
  boss: Enemy | null = null;
  /** Which pool boss this summon drew (seeded `boss` stream); null pre-summon. */
  bossDef: BossDef | null = null;
  /** Player-confining bounds during the boss fight (bullets still use walls). */
  arena: Bounds | null = null;
  bossInvuln = 0; // steps
  bossDefeated = false;
  private bossPhaseIdx = 0;
  private circleSpawned = false;

  // ---- Vault ("Reliquary") state — only used when opts.vault is set.
  readonly vault: boolean;
  /** The Keeper. NEVER assigned to `boss` (that path wins the run). */
  keeper: Enemy | null = null;
  vaultBossDefeated = false;
  /** What the alcove holds this vault: altar or chest (null = non-vault). */
  readonly vaultContent: 'altar' | 'chest' | null = null;
  /** Cleansing altar: world position, hold-channel progress, one use. */
  altarPos: { x: number; y: number } | null = null;
  altarChannel = 0;
  altarUsed = false;
  /** Alcove chest (vaultContent 'chest'): Keeper-gated, one open. */
  vaultChest: {
    x: number; y: number;
    drop: { artifactId: string | null; quality: number; proc: ArtifactProc; curseId: string | null };
    opened: boolean;
  } | null = null;
  private keeperSpawned = false;
  private arenaCenter: { x: number; y: number } | null = null;
  private keeperDrop: {
    artifactId: string | null; quality: number;
    proc: ArtifactProc; curseId: string | null;
  } | null = null;

  // ---- Treasure goblin (2026-07-28)
  /**
   * Pre-rolled goblin loot (ctor, 'goblin' stream). null = no goblin this
   * floor. `first` rides the enemy's drop* fields (normal killEnemy path);
   * `second` is pushed by killEnemy's goblin branch. Public: tests + scene
   * read it.
   */
  goblinDrop: {
    first: { artifactId: string | null; quality: number; proc: ArtifactProc; curseId: string | null };
    second: { artifactId: string; quality: number; proc: ArtifactProc; curseId: string | null } | null;
  } | null = null;

  private readonly registry = buildPatternRegistry();
  /** Ladder tier enemy-HP multiplier, cached once (identity 1 at tier 0). */
  private readonly tierHp: number;
  private nextEnemyId = 1;
  /** BFS distance-to-player field, recomputed periodically for enemy pathing. */
  private flowField: Int32Array | null = null;
  private flowTimer = 0;
  /** Gem value that couldn't spawn (pool full) — credited on next pickup. */
  private orphanGemValue = 0;
  /** Seconds until the next overtime enrage pulse (enrage.ts). */
  private enrageTimer = 0;
  /** Dev handle (?boss=<id>): overrides the seeded boss draw when it matches. */
  private readonly forceBoss: string | null;

  constructor(
    readonly map: GeneratedMap,
    readonly tileMap: TileMap,
    readonly floor: number,
    readonly player: Player,
    readonly runState: RunState,
    readonly events: EventBus,
    opts: { forceVaultDoor?: boolean; vault?: boolean; forceBoss?: string | undefined } = {},
  ) {
    this.vault = opts.vault ?? false;
    this.forceBoss = opts.forceBoss ?? null;
    this.tierHp = tierHpMult(runState.tier);
    this.bounds = {
      minX: TILE, minY: TILE,
      maxX: (map.w - 1) * TILE, maxY: (map.h - 1) * TILE,
    };
    // Aspect setup: heal gating + the hound (worlds are per-floor, so the
    // hound "revives on descent" simply by being rebuilt here).
    player.healBlock = aspectHealBlock(runState);
    if (runState.character === 'bramGaunt') {
      this.hound = {
        x: player.x + 40, y: player.y, hp: HOUND_HP, maxHp: HOUND_HP, alive: true,
        state: 'follow', stateLeft: 0, dashVx: 0, dashVy: 0,
        charge: 2, chipTick: 0.5, biteTick: 0.5, dashHit: new Set(),
      };
    }
    // Mourne (2026-07-27): the swarm can always BOOT — floor starts top up
    // the corpse reserve to a minimum (never clamps a bigger carried one).
    if (runState.character === 'mourne' && !opts.vault) {
      runState.corpseReserve = Math.max(runState.corpseReserve, MOURNE_FLOOR_RESERVE);
    }
    if (this.vault) {
      // Vault mode: NO trash schedule, NO floor events. Critically this skips
      // buildSchedule/rollFloorEvents entirely — running either here would
      // re-consume the origin floor's streams and re-mutate droppedArtifacts.
      this.director = new SpawnDirector([], map.spawnRegions);
      this.floorEvents = [];
      const arena = map.spawnRegions[0];
      if (arena) {
        this.arenaCenter = { x: (arena.cx + 0.5) * TILE, y: (arena.cy + 0.5) * TILE };
      }
      // Contents roll: its OWN stream (origin floor) so the keeper's
      // vaultLoot draws stay byte-identical whichever way it lands.
      this.vaultContent = runState.streams.stream('vaultContent', floor).bool() ? 'altar' : 'chest';
      // vaultLoot FIXED DRAW ORDER: keeper id[, quality, proc] first,
      // ALWAYS; then — only when vaultContent === 'chest' — chest id[,
      // quality, proc]. The chest branch is deterministic because the
      // content roll above is seeded. Both floors are Keeper-grade (≥100%).
      const vaultLootRng = runState.streams.stream('vaultLoot', floor);
      const artifactId = rollArtifactDrop(vaultLootRng, runState.droppedArtifacts);
      const quality = artifactId !== null
        ? rollQuality(vaultLootRng, Math.max(1.0, tierQualityFloor(runState.tier)))
        : 0;
      const procRoll = artifactId !== null
        ? rollArtifactProc(vaultLootRng)
        : { proc: 'none' as const, curseId: null };
      this.keeperDrop = { artifactId, quality, proc: procRoll.proc, curseId: procRoll.curseId };
      const altar = (map as { altar?: { x: number; y: number } }).altar;
      if (altar) {
        const ax = (altar.x + 0.5) * TILE;
        const ay = (altar.y + 0.5) * TILE;
        if (this.vaultContent === 'altar') {
          this.altarPos = { x: ax, y: ay };
        } else {
          const chestId = rollArtifactDrop(vaultLootRng, runState.droppedArtifacts);
          const chestQ = chestId !== null
            ? rollQuality(vaultLootRng, Math.max(1.0, tierQualityFloor(runState.tier)))
            : 0;
          const chestProc = chestId !== null
            ? rollArtifactProc(vaultLootRng)
            : { proc: 'none' as const, curseId: null };
          this.vaultChest = {
            x: ax, y: ay, opened: false,
            drop: { artifactId: chestId, quality: chestQ, proc: chestProc.proc, curseId: chestProc.curseId },
          };
        }
      }
    } else {
      const spawnRng = runState.streams.stream('spawns', floor);
      const lootRng = runState.streams.stream('loot', floor);
      this.director = new SpawnDirector(
        buildSchedule(
          spawnRng, floor, map.spawnRegions.length, lootRng,
          runState.droppedArtifacts, runState.tier,
        ),
        map.spawnRegions,
      );
      // MUST stay after buildSchedule: both mutate the run-wide droppedArtifacts
      // pool, and this statement order IS the cross-stream determinism contract.
      this.floorEvents = rollFloorEvents(
        runState.streams.stream('events', floor), floor, map,
        runState.droppedArtifacts, opts.forceVaultDoor ?? false, runState.tier,
        player.healBlock,
      );
      // Treasure goblin (floors 3+, ~8%): its OWN stream. MUST stay after
      // rollFloorEvents — this block also mutates the run-wide
      // droppedArtifacts pool, extending the ordering contract to three
      // members: buildSchedule → rollFloorEvents → goblin. Existence roll
      // ALWAYS consumed on eligible floors; loot draws branch only on the
      // (seed-deterministic) existence outcome. Placement is DRAW-FREE.
      if (floor >= GOBLIN_MIN_FLOOR) {
        const goblinRng = runState.streams.stream('goblin', floor);
        if (goblinRng.next() < GOBLIN_CHANCE) {
          // Vault-chest grade quality floor (≥100%), like strongbox/Keeper.
          const qFloor = Math.max(1.0, tierQualityFloor(runState.tier));
          const firstId = rollArtifactDrop(goblinRng, runState.droppedArtifacts);
          const firstQ = firstId !== null ? rollQuality(goblinRng, qFloor) : 0;
          const firstProc = firstId !== null
            ? rollArtifactProc(goblinRng)
            : { proc: 'none' as const, curseId: null };
          const first = {
            artifactId: firstId, quality: firstQ,
            proc: firstProc.proc, curseId: firstProc.curseId,
          };
          // Second-drop chance roll ALWAYS consumed once the goblin exists.
          let second = null;
          if (goblinRng.next() < goblinSecondDropChance(floor, runState.tier)) {
            const secondId = rollArtifactDrop(goblinRng, runState.droppedArtifacts);
            if (secondId !== null) {
              const secondQ = rollQuality(goblinRng, qFloor);
              const p = rollArtifactProc(goblinRng);
              second = {
                artifactId: secondId, quality: secondQ,
                proc: p.proc, curseId: p.curseId,
              };
            }
          }
          this.goblinDrop = { first, second };
          // Draw-free placement: farthest spawn region from the entrance.
          // +1 outside the spawn budget (like ambush/mites), cap-guarded.
          let best = map.spawnRegions[0];
          let bestD = -1;
          for (const r of map.spawnRegions) {
            const d = (r.cx - map.entrance.x) ** 2 + (r.cy - map.entrance.y) ** 2;
            if (d > bestD) { bestD = d; best = r; }
          }
          if (best && this.enemies.length < HARD_ENEMY_CAP) {
            const g = new Enemy(
              this.nextEnemyId++, 'goblin', ENEMY_DEFS.goblin,
              enemyHpMult(floor) * this.tierHp,
            );
            g.place((best.cx + 0.5) * TILE, (best.cy + 0.5) * TILE);
            if (first.artifactId !== null) {
              g.dropArtifactId = first.artifactId;
              g.dropQuality = first.quality;
              g.dropProc = first.proc;
              g.dropCurseId = first.curseId;
            }
            this.enemies.push(g);
          }
        }
      }
      // Geode scatter (ME3): own stream, FIXED draws — 1 count + n×(x,y);
      // snapping/skipping are draw-free. Never rolls artifacts, so it has no
      // droppedArtifacts contact and no cross-stream ordering hazard.
      // Hazards (ME4): own stream, biome-gated, floors ≥2 only. Like
      // scatter, zero droppedArtifacts contact ⇒ no ordering hazard.
      if (floor >= 2) {
        this.hazards = rollHazards(runState.streams.stream('hazards', floor), map);
      }
      const scatterRng = runState.streams.stream('scatter', floor);
      const n = 4 + scatterRng.int(4);
      for (let i = 0; i < n; i++) {
        const rx = scatterRng.int(map.w);
        const ry = scatterRng.int(map.h);
        const t = nearestFloor(map.tiles, map.w, map.h, rx, ry);
        const nearTo = (p: { x: number; y: number }): boolean =>
          Math.abs(t.x - p.x) <= 3 && Math.abs(t.y - p.y) <= 3;
        if (nearTo(map.entrance) || nearTo(map.exit)) continue;
        const gx = TileMap_toWorld(t.x);
        const gy = TileMap_toWorld(t.y);
        if (this.geodes.some((g) => g.x === gx && g.y === gy)) continue;
        this.geodes.push({ x: gx, y: gy, broken: false });
      }
    }
    this.enemyBullets.wallTest = (x, y) => tileMap.isWallAt(x, y);
    this.playerShots.wallTest = (x, y) => tileMap.isWallAt(x, y);
    this.patternCtx = {
      playerX: player.x,
      playerY: player.y,
      bullets: this.enemyBullets,
      telegraphs: this.telegraphs,
    };
  }

  update(dt: number, input: InputSnapshot): void {
    const player = this.player;
    this.syncPlayerStats();
    player.update(dt, input, this.arena ?? this.bounds);
    this.patternCtx.playerX = player.x;
    this.patternCtx.playerY = player.y;

    // Run clock ticks everywhere, vault included (its time is run time).
    // timeSeconds is run-monotonic, so the crossing fires exactly once per
    // run even across floor/vault world swaps.
    const wasOver = this.runState.timeSeconds >= RUN_TIME_LIMIT_SECONDS;
    this.runState.timeSeconds += dt;
    if (!wasOver && this.runState.timeSeconds >= RUN_TIME_LIMIT_SECONDS) {
      this.events.emit('enrageStarted', {});
    }

    if (this.vault) {
      // No summoning in the vault — empty circleCandidates alone is NOT
      // enough (tickSummoning falls back to map.exit, which here is the
      // return door). The origin world's circle forms after walking out.
      this.tickKeeper();
      this.tickAltar(dt, input);
    } else {
      this.tickSummoning(dt, input);
    }
    const bossActive = this.boss !== null && this.boss.alive;
    if (!bossActive && !this.vault) {
      this.director.tick(this.enemies.length, (ev) => this.spawnWave(ev));
      this.tickEnrage(dt);
    }
    if (bossActive) this.tickBossPhases();
    if (this.bossInvuln > 0) this.bossInvuln--;

    if (--this.flowTimer <= 0) {
      this.flowTimer = 15;
      const ptx = Math.floor(player.x / TILE);
      const pty = Math.floor(player.y / TILE);
      if (!this.tileMap.isWallTile(ptx, pty)) {
        this.flowField = bfsDistances(this.map.tiles, this.map.w, this.map.h, ptx, pty);
      }
    }
    for (const e of this.enemies) updateEnemy(e, this, dt);
    this.tickGoblin(dt);
    this.recycleFarEnemies();

    this.enemyBullets.update(dt, player.x, player.y, this.bounds);
    this.playerShots.update(dt, player.x, player.y, this.bounds);
    this.particles.update(dt, player.x, player.y, this.bounds);
    this.updateGems(dt);
    this.tickPickupBumps();

    this.telegraphs.update((zone) => {
      if (dist2(zone.x, zone.y, player.x, player.y) <= zone.radius * zone.radius) {
        this.damagePlayer(zone.damage);
      }
    });

    this.hash.rebuild(this.enemies);
    this.abilitySystem.update(this, dt);
    tickAspects(this, dt);
    tickActives(this, dt, input);
    this.brandQuiet += dt; // Brand's quiet-hit clock (reset when kills feed it)
    this.tickZones(dt);
    this.tickHazards(dt);
    this.tickWither(dt);
    this.tickDebuffs(dt);
    this.tickFindables(dt);
    this.tickDamageNumbers(dt);
    this.collideBulletsVsPlayer();
    this.collideShotsVsEnemies();
    this.tickGeodes();
    this.collideContact();
    this.tickCurseAura();
    this.pickupArtifacts();

    // S3: drain on-kill triggers iteratively — a trigger that kills pushes
    // more corpses onto the same queue, so chains resolve without recursion.
    this.drainingCorpses = true;
    while (this.pendingCorpses.length > 0) {
      skillOnKill(this, this.pendingCorpses.shift()!);
    }
    this.drainingCorpses = false;

    // Compact dead enemies (swap-remove).
    for (let i = this.enemies.length - 1; i >= 0; i--) {
      if (!this.enemies[i]!.alive) {
        this.enemies[i] = this.enemies[this.enemies.length - 1]!;
        this.enemies.pop();
      }
    }
  }

  // -------------------------------------------------------------- spawning

  private spawnWave(ev: SpawnEvent): void {
    // Reactive placement (design: schedule seeded, placement reactive): the
    // seeded regionIndex picks among rooms NEAR the player, so pressure stays
    // on-screen-adjacent instead of piling up in far rooms.
    const regions = this.director.regions;
    const near = regions.filter((r) => {
      const rx = (r.cx + 0.5) * TILE;
      const ry = (r.cy + 0.5) * TILE;
      return dist2(rx, ry, this.player.x, this.player.y) < 1100 * 1100;
    });
    const pool = near.length > 0 ? near : regions;
    const region = pool[ev.regionIndex % pool.length] ?? regions[0];
    if (!region) return;
    for (let i = 0; i < ev.count; i++) {
      // Reactive placement: seeded region, unseeded tile, off-screen bias.
      let x = 0;
      let y = 0;
      let ok = false;
      for (let tries = 0; tries < 12 && !ok; tries++) {
        const tx = region.x + 1 + gameplayRng.int(Math.max(1, region.w - 2));
        const ty = region.y + 1 + gameplayRng.int(Math.max(1, region.h - 2));
        if (this.tileMap.isWallTile(tx, ty)) continue;
        x = TileMap_toWorld(tx);
        y = TileMap_toWorld(ty);
        const far = dist2(x, y, this.player.x, this.player.y) > 420 * 420;
        if (far || tries >= 8) ok = true;
      }
      if (!ok) continue;

      const def = ENEMY_DEFS[ev.type];
      const enemy = new Enemy(this.nextEnemyId++, ev.type, def, enemyHpMult(this.floor) * this.tierHp);
      enemy.place(x, y);
      if (ev.elite && ev.affix) this.dressElite(enemy, ev.affix, ev);
      if (def.firePattern) {
        enemy.runner = new PhaseRunner(
          this.registry,
          [[{ loop: [{ pattern: def.firePattern }, { wait: def.fireCooldown }] }]],
          this.runState.streams.patternStream(this.floor, ev.ordinal * 8 + i),
          toSteps,
        );
      }
      this.enemies.push(enemy);
    }
  }

  /** The one elite recipe — shared by scheduled elites and ambush events. */
  private dressElite(
    enemy: Enemy,
    affix: EliteAffix,
    drop: {
      dropArtifactId: string | null; dropQuality: number;
      dropProc: ArtifactProc; dropCurseId: string | null;
    },
  ): void {
    enemy.affix = affix;
    enemy.dropArtifactId = drop.dropArtifactId;
    enemy.dropQuality = drop.dropQuality;
    enemy.dropProc = drop.dropProc;
    enemy.dropCurseId = drop.dropCurseId;
    enemy.radius *= 1.3;
    enemy.hp = enemy.maxHp = Math.round(enemy.maxHp * 2.4);
    enemy.gemValue *= 3;
    if (affix === 'fast') enemy.speed *= 1.55;
    if (affix === 'shielded') enemy.shieldHp = Math.round(enemy.maxHp * 0.5);
    if (affix === 'gemRich') enemy.gemValue *= 3;
  }

  /**
   * Summon lock (2026-07-27): once the circle stands (bar full) the floor
   * is a commitment — descending to a fresh, under-spawned floor made the
   * boss arena trivially easier. Locks the exit AND Cask's fuse; the
   * Reliquary door stays open (it returns to this same floor). A win ends
   * the run, so nothing needs unlocking after.
   */
  get descentLocked(): boolean {
    return this.circle !== null || (this.boss?.alive ?? false);
  }

  // -------------------------------------------------------------- events

  /** Open a loot-chest event: surface its pre-rolled contents. */
  openChest(ev: ChestEvent): void {
    if (ev.opened) return;
    ev.opened = true;
    relightCandle(this.runState); // Wick: content interactions refuel
    const x = TileMap_toWorld(ev.pos.x);
    const y = TileMap_toWorld(ev.pos.y);
    switch (ev.content) {
      case 'artifact':
        if (ev.drop.artifactId !== null) {
          this.artifactDrops.push({
            x, y, artifactId: ev.drop.artifactId, quality: ev.drop.quality,
            proc: ev.drop.proc, curseId: ev.drop.curseId, suppressed: false,
          });
        } else {
          // Drop pool exhausted (deterministic) — gems instead.
          this.spawnGems(x, y, 40);
        }
        this.events.emit('chestOpened', { artifactId: ev.drop.artifactId, content: 'artifact' });
        break;
      case 'gems':
        // Floor-scaled like every kill drop, else the hoard decays to trash
        // value on deep floors. ≈0.75× the boss's base 120 — a real prize.
        this.spawnGems(x, y, Math.round(CHEST_HOARD_BASE * gemMultiplier(this.floor)));
        this.events.emit('chestOpened', { artifactId: null, content: 'gems' });
        break;
      case 'mend':
        if (this.runState.pocket === null) {
          this.runState.pocket = 'mend';
          this.events.emit('chestOpened', { artifactId: null, content: 'mend' });
        } else {
          // Pocket already stocked — modest consolation, below the hoard.
          this.spawnGems(x, y, 40);
          this.events.emit('chestOpened', { artifactId: null, content: 'gems' });
        }
        break;
    }
  }

  /**
   * Open the vault's alcove chest (vaultContent 'chest'). Keeper-gated
   * exactly like the altar: inert until vaultBossDefeated. One open; the
   * per-floor vaultWorld cache makes that re-entry-proof.
   */
  openVaultChest(): void {
    const c = this.vaultChest;
    if (!c || c.opened || !this.vaultBossDefeated) return;
    c.opened = true;
    relightCandle(this.runState); // Wick: content interactions refuel
    if (c.drop.artifactId !== null) {
      this.artifactDrops.push({
        x: c.x, y: c.y, artifactId: c.drop.artifactId, quality: c.drop.quality,
        proc: c.drop.proc, curseId: c.drop.curseId, suppressed: false,
      });
    } else {
      this.spawnGems(c.x, c.y, 60); // pool exhausted — consolation
    }
    this.events.emit('chestOpened', { artifactId: c.drop.artifactId, content: 'artifact' });
  }

  // ---------------------------------------------------------- findables (ME3)

  /** Pick up the bone key: arms this floor's strongbox. */
  takeKey(ev: BoneKeyEvent): void {
    if (ev.taken) return;
    ev.taken = true;
    this.keyHeld = true;
    relightCandle(this.runState); // Wick: content interactions refuel
    this.events.emit('keyTaken', { x: TileMap_toWorld(ev.pos.x), y: TileMap_toWorld(ev.pos.y) });
  }

  /** Open the strongbox — inert without the key (the hint says why). */
  openStrongbox(ev: StrongboxEvent): void {
    if (ev.opened || !this.keyHeld) return;
    ev.opened = true;
    this.keyHeld = false;
    relightCandle(this.runState);
    const x = TileMap_toWorld(ev.pos.x);
    const y = TileMap_toWorld(ev.pos.y);
    if (ev.drop.artifactId !== null) {
      this.artifactDrops.push({
        x, y, artifactId: ev.drop.artifactId, quality: ev.drop.quality,
        proc: ev.drop.proc, curseId: ev.drop.curseId, suppressed: false,
      });
    } else {
      this.spawnGems(x, y, 60); // pool exhausted — consolation
    }
    this.events.emit('chestOpened', { artifactId: ev.drop.artifactId, content: 'artifact' });
  }

  /**
   * Light one relay brazier. The first light starts the shared window; all
   * three lit before it expires unseals the reward chest at brazier 0.
   */
  lightBrazier(ev: BrazierRelayEvent, idx: number): void {
    if (ev.unsealed || ev.lit[idx]) return;
    ev.lit[idx] = true;
    relightCandle(this.runState); // Wick: it is literal fire
    // First light arms the BFS-route-derived window (rolled at floor start).
    if (ev.window <= 0) ev.window = ev.windowTotal;
    const lit = ev.lit.filter(Boolean).length;
    if (lit === ev.lit.length) {
      ev.unsealed = true;
      ev.window = 0;
      // The chest unseals at the LAST-lit brazier (2026-07-28): the payoff
      // lands where you finish, no backtracking to the anchor. Draw-free —
      // the content was rolled at floor start, only the position moves.
      ev.chest.pos = ev.braziers[idx] ?? ev.pos;
      this.events.emit('relayUnsealed', {
        x: TileMap_toWorld(ev.chest.pos.x), y: TileMap_toWorld(ev.chest.pos.y),
      });
    } else {
      this.events.emit('brazierLit', { lit, total: ev.lit.length, window: ev.window });
    }
  }

  /** One drink; the NORMAL heal path, so heal blocks apply (Vane: nothing). */
  useSpring(ev: SpringEvent): void {
    if (ev.used) return;
    ev.used = true;
    relightCandle(this.runState);
    const healed = this.player.heal(2);
    this.events.emit('springUsed', { healed });
  }

  /** Push a floating number; drops the oldest past the cap (headless guard). */
  private pushDamagePopup(x: number, y: number, amount: number, kind: 'hit' | 'dot' | 'taken' | 'burn'): void {
    this.damagePopups.push({ x, y, amount, kind });
    if (this.damagePopups.length > 64) this.damagePopups.shift();
  }

  /**
   * Flush enemy damage accumulators whose window elapsed. Sub-1 sums keep
   * accumulating (a "0" popup is noise); positions track the enemy.
   */
  private tickDamageNumbers(dt: number): void {
    for (const [id, a] of this.dmgAcc) {
      a.ttl -= dt;
      if (a.ttl > 0) continue;
      if (a.sum >= 1) {
        this.pushDamagePopup(a.x, a.y, Math.round(a.sum), a.hasHit ? 'hit' : 'dot');
        this.dmgAcc.delete(id);
      } else {
        a.ttl = DMG_POPUP_WINDOW; // keep gathering until it reads as ≥1
      }
    }
  }

  /** Relay window countdown — rng-free; expiry resets the braziers (retryable). */
  private tickFindables(dt: number): void {
    for (const ev of this.floorEvents) {
      if (ev.type !== 'brazierRelay' || ev.unsealed || ev.window <= 0) continue;
      ev.window -= dt;
      if (ev.window <= 0) {
        ev.window = 0;
        ev.lit.fill(false);
        this.events.emit('relayReset', {});
      }
    }
  }

  /**
   * Geodes shatter on player-shot contact OR a body bump (walking into one
   * cracks it — every build can reach them, projectiles or not; playtest
   * fix 2026-07-26) and pay a floor-scaled gem burst through the normal
   * spawnGems funnel. Shots SURVIVE — scenery must never eat damage output.
   * ≤8 geodes/floor keeps this loop trivial.
   */
  private tickGeodes(): void {
    if (this.geodes.length === 0) return;
    const pool = this.playerShots;
    const pl = this.player;
    // 0.7 = the movement-collision shrink (Player.update's moveResolver).
    const bumpR = GEODE_RADIUS + pl.radius * 0.7;
    for (const g of this.geodes) {
      if (g.broken) continue;
      let hit = pl.alive && dist2(pl.x, pl.y, g.x, g.y) <= bumpR * bumpR;
      for (let i = 0; !hit && i < pool.count; i++) {
        const rr = GEODE_RADIUS + pool.radius[i]!;
        hit = dist2(pool.x[i]!, pool.y[i]!, g.x, g.y) <= rr * rr;
      }
      if (hit) {
        g.broken = true;
        this.spawnGems(g.x, g.y, Math.round(GEODE_GEMS_BASE * gemMultiplier(this.floor)));
        this.spawnSparks(g.x, g.y, 8, 1.1);
        this.events.emit('geodeShattered', { x: g.x, y: g.y });
      }
    }
  }

  /**
   * Spring a triple-elite ambush: the pre-rolled elites spawn directly around
   * the marker (reactive placement). Deliberately bypasses the director's
   * LIVE_CAP deferral — the player opted into a burst; +3 over cap is
   * momentary, and deferring would make the trigger feel broken.
   */
  triggerAmbush(ev: AmbushEvent): void {
    if (ev.triggered) return;
    ev.triggered = true;
    const cx = TileMap_toWorld(ev.pos.x);
    const cy = TileMap_toWorld(ev.pos.y);
    ev.elites.forEach((spec, i) => {
      let x = cx;
      let y = cy;
      const def = ENEMY_DEFS[spec.enemyType];
      // Clearance check uses the DRESSED elite radius (×1.3) at resolveMove's
      // 0.8 shrink — a point check can wedge a body into a wall forever.
      const clearR = def.radius * 1.3 * 0.8;
      for (let tries = 0; tries < 16; tries++) {
        const ang = gameplayRng.range(0, TAU);
        const d = gameplayRng.range(60, 140);
        const tx = cx + Math.cos(ang) * d;
        const ty = cy + Math.sin(ang) * d;
        if (!this.tileMap.circleCollides(tx, ty, clearR)) {
          x = tx;
          y = ty;
          break;
        }
      }
      const enemy = new Enemy(this.nextEnemyId++, spec.enemyType, def, enemyHpMult(this.floor) * this.tierHp);
      enemy.place(x, y);
      this.dressElite(enemy, spec.affix, spec);
      if (def.firePattern) {
        enemy.runner = new PhaseRunner(
          this.registry,
          [[{ loop: [{ pattern: def.firePattern }, { wait: def.fireCooldown }] }]],
          this.runState.streams.patternStream(this.floor, ev.ordinalBase + i),
          toSteps,
        );
      }
      this.enemies.push(enemy);
    });
    this.events.emit('ambushTriggered', { x: cx, y: cy });
  }

  /**
   * Direction an enemy should move to reach the player: straight chase when
   * line-of-sight is clear, otherwise descend the BFS flow field around
   * walls. Returns a normalized vector.
   */
  chaseDir(ex: number, ey: number): [number, number] {
    const p = this.player;
    const dx = p.x - ex;
    const dy = p.y - ey;
    const m = Math.hypot(dx, dy) || 1;
    if (!this.flowField || !this.tileMap.lineBlocked(ex, ey, p.x, p.y)) {
      return [dx / m, dy / m];
    }
    const w = this.map.w;
    const tx = Math.floor(ex / TILE);
    const ty = Math.floor(ey / TILE);
    let bestDist = Infinity;
    let bestX = tx;
    let bestY = ty;
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        if (ox === 0 && oy === 0) continue;
        const nx = tx + ox;
        const ny = ty + oy;
        if (this.tileMap.isWallTile(nx, ny)) continue;
        // No diagonal corner-cutting.
        if (ox !== 0 && oy !== 0 &&
          (this.tileMap.isWallTile(tx + ox, ty) || this.tileMap.isWallTile(tx, ty + oy))) continue;
        const d = this.flowField[ny * w + nx]!;
        if (d >= 0 && d < bestDist) {
          bestDist = d;
          bestX = nx;
          bestY = ny;
        }
      }
    }
    if (bestDist === Infinity) return [dx / m, dy / m];
    const gx = (bestX + 0.5) * TILE - ex;
    const gy = (bestY + 0.5) * TILE - ey;
    const gm = Math.hypot(gx, gy) || 1;
    return [gx / gm, gy / gm];
  }

  /**
   * Treasure goblin escape clock. Ticked World-side, NOT in behaviors — the
   * ACTIVE_RANGE early-out there would freeze the timer whenever the player
   * walks away, and the escape must fire regardless.
   */
  private tickGoblin(dt: number): void {
    for (const e of this.enemies) {
      if (e.type !== 'goblin' || !e.alive || e.panicLeft <= 0) continue;
      e.panicLeft -= dt;
      if (e.panicLeft <= 0) {
        // Escape — deliberately NOT killEnemy: no drops, no gems, no bar
        // fill, no corpse. The reserved artifact ids stay out of the run
        // pool (same class as an unopened Keeper chest).
        e.alive = false;
        this.dmgAcc.delete(e.id); // no ghost damage number after the puff
        this.spawnSparks(e.x, e.y, 18, 1.4);
        this.events.emit('goblinEscaped', { x: e.x, y: e.y });
      }
    }
  }

  /** Arm the goblin's escape clock once; proximity OR first damage, whichever first. */
  panicGoblin(e: Enemy): void {
    if (e.type !== 'goblin' || e.panicLeft > 0 || !e.alive) return;
    e.panicLeft = GOBLIN_PANIC_SECONDS;
    this.events.emit('goblinPanicked', { x: e.x, y: e.y });
  }

  /**
   * Flee direction for prey (goblin): chaseDir's mirror. ASCENDS the BFS
   * flow field (distance-to-player), so the escape routes around walls
   * instead of backpedaling into them. At a local maximum (cornered in a
   * dead end) it slides tangentially — the player's earned kill window,
   * not a freeze.
   */
  fleeDir(e: Enemy): [number, number] {
    const p = this.player;
    const dx = e.x - p.x;
    const dy = e.y - p.y;
    const m = Math.hypot(dx, dy) || 1;
    if (!this.flowField) return [dx / m, dy / m];
    const w = this.map.w;
    const tx = Math.floor(e.x / TILE);
    const ty = Math.floor(e.y / TILE);
    const here = this.flowField[ty * w + tx] ?? -1;
    let bestDist = here;
    let bestX = tx;
    let bestY = ty;
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        if (ox === 0 && oy === 0) continue;
        const nx = tx + ox;
        const ny = ty + oy;
        if (this.tileMap.isWallTile(nx, ny)) continue;
        // No diagonal corner-cutting (same rule as chaseDir).
        if (ox !== 0 && oy !== 0 &&
          (this.tileMap.isWallTile(tx + ox, ty) || this.tileMap.isWallTile(tx, ty + oy))) continue;
        const d = this.flowField[ny * w + nx]!;
        if (d > bestDist) {
          bestDist = d;
          bestX = nx;
          bestY = ny;
        }
      }
    }
    if (bestDist <= here || here < 0) {
      // Cornered: tangential squirm along the wall.
      return [(-dy / m) * e.strafeDir, (dx / m) * e.strafeDir];
    }
    const gx = (bestX + 0.5) * TILE - e.x;
    const gy = (bestY + 0.5) * TILE - e.y;
    const gm = Math.hypot(gx, gy) || 1;
    return [gx / gm, gy / gm];
  }

  /**
   * Survivors-style recycling: enemies left far behind teleport to a floor
   * tile just off-screen from the player. Keeps pressure on the player and
   * stops the live cap from filling with unreachable stragglers.
   */
  private recycleFarEnemies(): void {
    const p = this.player;
    // Threshold sits just past the behavior ACTIVE_RANGE (900): an enemy too
    // far to act is recycled — no dead band where enemies idle forever.
    for (const e of this.enemies) {
      // The keeper is exempt like the boss: the vault diagonal exceeds the
      // 950px threshold, and a teleporting altar-guardian would be absurd.
      // The goblin too — a successful flight must never teleport it back
      // into melee range; its panic timer is the despawn path.
      if (e.type === 'boss' || e.type === 'keeper' || e.type === 'goblin' || !e.alive) continue;
      if (dist2(p.x, p.y, e.x, e.y) < 950 * 950) continue;
      let placed = false;
      for (let tries = 0; tries < 16 && !placed; tries++) {
        const ang = gameplayRng.range(0, TAU);
        const dist = gameplayRng.range(480, 800);
        const x = p.x + Math.cos(ang) * dist;
        const y = p.y + Math.sin(ang) * dist;
        // Circle test at resolveMove's shrink radius — a point check can
        // wedge the body into a wall it can never escape (perma-freeze).
        if (!this.tileMap.circleCollides(x, y, e.radius * 0.8)) {
          e.place(x, y);
          placed = true;
        }
      }
      if (!placed) {
        // Guaranteed fallback: a floor tile in some nearby room.
        const rooms = this.map.rooms;
        const room = rooms[gameplayRng.int(rooms.length)]!;
        e.place((room.cx + 0.5) * TILE, (room.cy + 0.5) * TILE);
      }
    }
  }

  // -------------------------------------------------------------- combat

  damagePlayer(damage: number): void {
    if (this.runState.god) return;
    // Ladder scaling at the single incoming-damage funnel (contact, bullets,
    // telegraph zones). tier 0 short-circuits to the exact pre-ladder path.
    const d = this.runState.tier > 0 ? scaleTierDamage(damage, this.runState.tier) : damage;
    // Wardstone (shop, 2026-07-27): a charge eats the hit ONLY where it
    // would land — i-framed contacts must not drain it. Checked BEFORE the
    // lethal save: a purchased ward must never waste Vane's refusal. Brief
    // i-frames stop a same-tick burst from chewing both charges at once.
    if (
      this.runState.wardCharges > 0 &&
      this.player.iframes <= 0 && this.player.alive
    ) {
      this.runState.wardCharges--;
      this.player.iframes = Math.round(0.4 * 60);
      this.events.emit('wardBlocked', { chargesLeft: this.runState.wardCharges });
      this.spawnSparks(this.player.x, this.player.y, 8, 1.2);
      return;
    }
    // Aspect lethal saves (Ninefold) intercept a killing blow.
    if (
      this.player.iframes <= 0 && this.player.alive &&
      this.player.hp <= d && aspectLethalSave(this, d)
    ) {
      return;
    }
    if (!this.player.hit(d)) return;
    this.events.emit('playerHit', { damage: d, hpAfter: this.player.hp });
    this.pushDamagePopup(this.player.x, this.player.y - 18, d, 'taken');
    this.spawnSparks(this.player.x, this.player.y, 10, 1.1);
    skillOnPlayerHit(this); // Recoil-class retaliation (M7)
    if (this.runState.character === 'barrow') {
      // Ironbound: pain becomes the weapon (aspects.ts ticks the aura).
      this.ironboundStacks = Math.min(6, this.ironboundStacks + 1);
      this.ironboundFade = 6;
    }
  }

  /**
   * Self-inflicted damage (Cask's blasts) = POWDER BURNS (M0 rework):
   * scorches max HP instead of dealing a hit — never lethal (effective max
   * floors at 1), so the boss duel can hurt him without his own gun killing
   * him. Ignores i-frames (the old hit-then-fuse free skip is gone), stays
   * NEVER tier-scaled, still no Recoil. Descent restores the CEILING only
   * (round 5): onDescend clears burnedHp but arms burnCreditHold, so
   * syncPlayerStats raises maxHp without crediting the HP delta.
   */
  selfDamage(amount: number): void {
    const rs = this.runState;
    if (rs.god) return;
    // Burns FLOOR at half max (2026-07-27): the cost is a bruise, not a
    // countdown — a long floor leaves him scorched, never doomed.
    const derived = Math.round(rs.stats.resolve('maxHp'));
    const ceiling = derived - Math.ceil(derived * CASK_BURN_FLOOR);
    const burn = Math.min(amount, Math.max(0, ceiling - rs.burnedHp));
    if (burn <= 0) return;
    rs.burnedHp += burn;
    this.syncPlayerStats(); // clamp hp against the scorched ceiling now
    this.events.emit('playerHit', { damage: burn, hpAfter: this.player.hp });
    this.pushDamagePopup(this.player.x, this.player.y - 18, burn, 'burn');
    this.spawnSparks(this.player.x, this.player.y, 8, 1.0);
  }

  /**
   * Bram & Gaunt (M0): Bram kneels at the body and brings Gaunt back at half
   * HP for 1 HP of his own (chip — no i-frame dodge). The gem forfeit already
   * happened at death; this ends the no-hound dead state, not the punishment.
   * Caller gates on player.hp > 1 so the kneel can't kill.
   */
  reviveHound(): void {
    const h = this.hound;
    if (!h || h.alive) return;
    h.alive = true;
    h.hp = Math.ceil(h.maxHp / 2);
    h.state = 'follow';
    h.stateLeft = 0;
    h.charge = 2;
    h.chipTick = 0.5;
    h.biteTick = 0.5;
    this.chipPlayer(1);
    this.events.emit('houndRevived', { x: h.x, y: h.y });
  }

  /**
   * Guttering chip (Wick at an empty candle): bypasses i-frames AND tier —
   * a slow burn, not a hit. Can kill.
   */
  chipPlayer(amount: number): void {
    if (this.runState.god || !this.player.alive) return;
    this.player.hp -= amount;
    this.events.emit('playerHit', { damage: amount, hpAfter: this.player.hp });
    this.pushDamagePopup(this.player.x, this.player.y - 18, amount, 'taken');
    if (this.player.hp <= 0) {
      this.player.hp = 0;
      this.player.alive = false;
    }
  }

  /**
   * The one player→enemy damage funnel. kind 'dot' marks continuous ticks
   * (zones, dots) — they take the amps but never stack Fracture shred.
   */
  damageEnemy(index: number, amount: number, kind: 'hit' | 'dot' = 'hit'): void {
    const e = this.enemies[index];
    if (!e || !e.alive) return;
    if (e.type === 'boss' && this.bossInvuln > 0) return; // phase-transition breath
    if (e.type === 'goblin') this.panicGoblin(e); // first damage spooks it (one-shot)
    // Debuff amps — all at the one funnel, no per-skill scatter.
    if (e.chillLeft > 0) amount *= RIME_AMP;
    if (e.brandLeft > 0) amount *= BRAND_AMP;
    if (e.shredStacks > 0) amount *= 1 + e.shredStacks * SHRED_PER_STACK;
    // Damage numbers: aggregate the POST-AMP total (what actually landed,
    // shield or flesh) — per-tick dots merge into one readable number.
    const acc = this.dmgAcc.get(e.id);
    if (acc) {
      acc.sum += amount;
      acc.x = e.x;
      acc.y = e.y;
      if (kind === 'hit') acc.hasHit = true;
    } else {
      this.dmgAcc.set(e.id, {
        x: e.x, y: e.y, sum: amount, ttl: DMG_POPUP_WINDOW, hasHit: kind === 'hit',
      });
    }
    if (e.shieldHp > 0) {
      e.shieldHp -= amount;
      e.flash = 2;
      if (e.shieldHp > 0) return;
      amount = -e.shieldHp; // overflow carries into hp
      e.shieldHp = 0;
    }
    e.hp -= amount;
    e.flash = 3;
    if (e.hp <= 0) {
      this.killEnemy(e);
      return;
    }
    // Deep Freeze: a chilled REGULAR (no elites, never boss/keeper) left
    // below the shatter threshold dies outright.
    if (
      e.chillLeft > 0 && e.chillShatter && e.affix === null &&
      e.type !== 'boss' && e.type !== 'keeper' &&
      e.hp < e.maxHp * RIME_SHATTER_FRAC
    ) {
      this.events.emit('shatter', { x: e.x, y: e.y });
      this.killEnemy(e);
      return;
    }
    // Fracture shred stacks on discrete hits only (this hit used the OLD
    // stack count; the new stack pays out on the next one).
    if (kind === 'hit') skillOnEnemyHit(this, index);
  }

  private killEnemy(e: Enemy): void {
    e.alive = false;
    // Killing blow: flush this enemy's aggregated number NOW (never <1).
    const acc = this.dmgAcc.get(e.id);
    if (acc) {
      this.dmgAcc.delete(e.id);
      this.pushDamagePopup(e.x, e.y, Math.max(1, Math.round(acc.sum)), acc.hasHit ? 'hit' : 'dot');
    }
    this.runState.kills++;
    const mult = e.type === 'boss' ? bossGemMult(this.floor) : gemMultiplier(this.floor);
    // KILL_GEM_NORM: ×2-density counterweight; boss/keeper/goblin counts
    // didn't double (the goblin's fat payout IS the point).
    const norm = e.type === 'boss' || e.type === 'keeper' || e.type === 'goblin'
      ? 1 : KILL_GEM_NORM;
    const value = Math.round(e.gemValue * mult * norm);
    this.spawnGems(e.x, e.y, value);
    if (e.type === 'boss') {
      this.bossDefeated = true;
      this.arena = null;
      this.enemyBullets.clear();
      // Vacuum-collect every floor gem (the boss's own shower spawned above
      // is already in the pool) — the run is won, nothing should linger.
      let total = this.orphanGemValue;
      for (let i = 0; i < this.gems.count; i++) total += this.gems.p1[i]!;
      this.orphanGemValue = 0;
      this.gems.clear();
      if (total > 0) {
        const value = total * this.runState.stats.resolve('gemGainMult');
        this.runState.collectGems(value);
        this.events.emit('gemCollected', { value });
      }
      this.events.emit('bossKilled', { x: e.x, y: e.y });
    }
    if (e.type === 'keeper') {
      this.vaultBossDefeated = true;
      this.enemyBullets.clear();
      this.events.emit('keeperKilled', { x: e.x, y: e.y });
    }
    this.spawnSparks(e.x, e.y, e.affix ? 26 : 14, e.affix ? 1.5 : 1.2);
    if (e.dropArtifactId) {
      this.artifactDrops.push({
        x: e.x, y: e.y, artifactId: e.dropArtifactId, quality: e.dropQuality,
        proc: e.dropProc, curseId: e.dropCurseId, suppressed: false,
      });
    }
    if (e.type === 'goblin') {
      // The bonus second drop rides World.goblinDrop (the Enemy record only
      // carries one) — pushed at an offset so the two don't stack visually.
      const second = this.goblinDrop?.second;
      if (second) {
        this.artifactDrops.push({
          x: e.x + 26, y: e.y, artifactId: second.artifactId, quality: second.quality,
          proc: second.proc, curseId: second.curseId, suppressed: false,
        });
      }
      this.events.emit('goblinKilled', { x: e.x, y: e.y });
    }
    if (e.type === 'splitter') this.burstSplitter(e);
    // Kill-bar fill: regulars ×1, elites/Keeper ×ELITE_FILL, boss/goblin
    // none (their kill IS the payoff; goblin zero-fill is also I1 armor).
    // Player-driven, zero rng — outside the seeded split.
    if (e.type !== 'boss' && e.type !== 'goblin') {
      const eliteClass = e.affix !== null || e.type === 'keeper';
      this.runState.addBarFill((eliteClass ? ELITE_FILL : REGULAR_FILL) * killFillMult(this.floor));
    }
    // S3: queue the corpse for on-kill skill triggers (drained iteratively in
    // update — a trigger that kills pushes more corpses onto this same queue).
    this.pendingCorpses.push({
      x: e.x, y: e.y, type: e.type, isElite: e.affix !== null,
      wasChilled: e.chillLeft > 0, wasBurning: e.burnLeft > 0,
      wasBranded: e.brandLeft > 0, killedByKind: e.lastHitKind,
      viaDetonation: this.drainingCorpses,
    });
    aspectOnKill(this, e.x, e.y); // Mourne: crawlers sprout at the corpse
    this.events.emit('enemyKilled', { x: e.x, y: e.y, gemValue: value, isElite: e.affix !== null });
  }

  /**
   * Splitter death-burst: 2 fast fragile mites at the corpse. Placement is
   * reactive/unseeded (gameplayRng) — kill order and death position are
   * gameplay-driven, so no seeded stream may be touched here; mites are
   * melee (no runner) precisely because a pattern would need a kill-order
   * stream key, which the determinism split forbids. Bypasses LIVE_CAP like
   * triggerAmbush, but cap-guarded (HARD_ENEMY_CAP) to stay under the hard
   * test ceiling (132).
   */
  private burstSplitter(parent: Enemy): void {
    for (let i = 0; i < 2; i++) {
      if (this.enemies.length >= HARD_ENEMY_CAP) break;
      let x = parent.x;
      let y = parent.y;
      for (let tries = 0; tries < 8; tries++) {
        const ang = gameplayRng.range(0, TAU);
        const d = gameplayRng.range(14, 30);
        const tx = parent.x + Math.cos(ang) * d;
        const ty = parent.y + Math.sin(ang) * d;
        // Circle test (resolveMove's shrink) — point checks wedge bodies.
        if (!this.tileMap.circleCollides(tx, ty, ENEMY_DEFS.mite.radius * 0.8)) {
          x = tx;
          y = ty;
          break;
        }
      }
      const mite = new Enemy(this.nextEnemyId++, 'mite', ENEMY_DEFS.mite, enemyHpMult(this.floor) * this.tierHp);
      mite.place(x, y);
      this.enemies.push(mite);
    }
  }

  /**
   * Overtime enrage (enrage.ts): past RUN_TIME_LIMIT_SECONDS, layer
   * escalating melee pressure on top of the schedule until the player dies
   * or summons the boss (caller gates on bossActive/vault). Consumes ZERO
   * seeded draws — timing is live-play-dependent, so like burstSplitter this
   * runs entirely on gameplayRng and spawns only runner-free melee types.
   * Normal gemValue is deliberate: overtime kills still feed the bar (the
   * do-or-die crescendo), the unbounded ramp is the counterweight.
   */
  private tickEnrage(dt: number): void {
    const over = this.runState.timeSeconds - RUN_TIME_LIMIT_SECONDS;
    if (over <= 0) return;
    this.enrageTimer -= dt;
    if (this.enrageTimer > 0) return;
    this.enrageTimer = enrageSpawnInterval(over);
    const p = this.player;
    const pack = enragePackSize(over);
    for (let i = 0; i < pack; i++) {
      if (this.enemies.length >= ENRAGE_SPAWN_CAP) break;
      const type = gameplayRng.pick(ENRAGE_TYPES);
      let placed = false;
      let x = 0;
      let y = 0;
      for (let tries = 0; tries < 16 && !placed; tries++) {
        const ang = gameplayRng.range(0, TAU);
        const dist = gameplayRng.range(480, 800);
        x = p.x + Math.cos(ang) * dist;
        y = p.y + Math.sin(ang) * dist;
        // Circle test (resolveMove's shrink) — point checks wedge bodies.
        if (!this.tileMap.circleCollides(x, y, ENEMY_DEFS[type].radius * 0.8)) placed = true;
      }
      if (!placed) continue;
      const enemy = new Enemy(this.nextEnemyId++, type, ENEMY_DEFS[type], enemyHpMult(this.floor) * this.tierHp);
      enemy.speed *= enrageSpeedMult(over);
      enemy.place(x, y);
      this.enemies.push(enemy);
    }
  }

  private collideBulletsVsPlayer(): void {
    const player = this.player;
    if (player.iframes > 0 || !player.alive) return;
    const pool = this.enemyBullets;
    const { x, y, px, py, radius } = pool;
    const hx = player.x;
    const hy = player.y;
    const hr = player.hitRadius;
    for (let i = 0; i < pool.count; i++) {
      const rr = radius[i]! + hr;
      const bx = x[i]!;
      const by = y[i]!;
      const bpx = px[i]!;
      const bpy = py[i]!;
      const minX = (bx < bpx ? bx : bpx) - rr;
      const maxX = (bx > bpx ? bx : bpx) + rr;
      const minY = (by < bpy ? by : bpy) - rr;
      const maxY = (by > bpy ? by : bpy) + rr;
      if (hx < minX || hx > maxX || hy < minY || hy > maxY) continue;
      if (segPointDist2(bpx, bpy, bx, by, hx, hy) <= rr * rr) {
        pool.despawn(i);
        this.damagePlayer(1);
        return; // i-frames now active; the rest pass through
      }
    }
  }

  private collideShotsVsEnemies(): void {
    const pool = this.playerShots;
    for (let i = 0; i < pool.count; i++) {
      const sx = pool.x[i]!;
      const sy = pool.y[i]!;
      const sr = pool.radius[i]!;
      let hit = -1;
      this.hash.query(sx, sy, sr + 32, (idx) => {
        if (hit >= 0) return;
        const e = this.enemies[idx];
        if (!e || !e.alive) return;
        const rr = e.radius + sr;
        if (dist2(sx, sy, e.x, e.y) <= rr * rr) hit = idx;
      });
      if (hit >= 0) {
        // Stamp the direct hit's kind so a resulting corpse knows its killer
        // (Shatterline needs "killed BY a shard"); cleared below — splash and
        // later damage must not inherit it.
        this.enemies[hit]!.lastHitKind = pool.kind[i]!;
        this.damageEnemy(hit, pool.p0[i]! || 4);
        this.enemies[hit]!.lastHitKind = -1;
        const killed = !this.enemies[hit]!.alive;
        // Splash: p2 carries the burst radius (0 = plain shot). The direct
        // target already took full damage; neighbors take 60%.
        const splash = pool.p2[i]!;
        if (splash > 0) {
          const splashDmg = (pool.p0[i]! || 4) * 0.6;
          this.hash.query(sx, sy, splash + 30, (idx) => {
            if (idx === hit) return;
            const e = this.enemies[idx];
            if (!e || !e.alive) return;
            if (dist2(sx, sy, e.x, e.y) <= (splash + e.radius) ** 2) {
              this.damageEnemy(idx, splashDmg);
            }
          });
          this.spawnSparks(sx, sy, 6, 1.0);
          this.events.emit('splashBurst', { x: sx, y: sy, radius: splash });
        }
        // Bore-charge: REAL friendly fire on Cask — never tier-scaled.
        // Safe rim (2026-07-27): only the blast CORE burns him; standing at
        // the edge of your own explosion is the skill expression.
        if (pool.kind[i] === SHOT_KINDS.bore && splash > 0) {
          const pl = this.player;
          const core = splash * CASK_SAFE_RIM;
          if (dist2(sx, sy, pl.x, pl.y) <= (core + pl.hitRadius) ** 2) {
            this.selfDamage(1);
          }
        }
        // Impact behavior by kind (shots.ts): charges ride in p1.
        const behavior = shotBehavior(pool.kind[i]!);
        let survives = false;
        if (behavior === 'bounce' && pool.p1[i]! > 0) {
          // Windfall: a killing bounce refunds its charge.
          const windfall = killed &&
            this.runState.abilities.some((a) => a.defId === 'ricochet' && a.upgraded);
          if (!windfall) pool.p1[i] = pool.p1[i]! - 1;
          survives = this.retargetShot(i, hit);
        } else if (behavior === 'moteBounce' && killed && pool.p1[i]! > 0) {
          pool.p1[i] = pool.p1[i]! - 1;
          survives = this.retargetShot(i, hit);
        } else if (behavior === 'pierce' && pool.p1[i]! > 0) {
          // Hollow Shot: keep flying — step past the pierced body so the
          // next tick doesn't re-hit it in place.
          pool.p1[i] = pool.p1[i]! - 1;
          const speed = Math.hypot(pool.vx[i]!, pool.vy[i]!) || 1;
          const stepX = pool.vx[i]! / speed;
          const stepY = pool.vy[i]! / speed;
          const clear = this.enemies[hit]!.radius * 2 + pool.radius[i]! + 4;
          pool.x[i] = sx + stepX * clear;
          pool.y[i] = sy + stepY * clear;
          survives = true;
        } else if (behavior === 'split' && pool.p1[i]! > 0) {
          // Prism Lance: the beam refracts — two children, one fewer
          // generation, ×0.7 damage each.
          const heading = Math.atan2(pool.vy[i]!, pool.vx[i]!);
          const speed = Math.hypot(pool.vx[i]!, pool.vy[i]!) || 1;
          const childDamage = (pool.p0[i]! || 4) * 0.7;
          const gens = pool.p1[i]! - 1;
          for (const off of [-0.45, 0.45]) {
            spawnPlayerShot(
              this, sx + Math.cos(heading + off) * 20, sy + Math.sin(heading + off) * 20,
              heading + off, speed, SHOT_KINDS.prism, childDamage,
              { radius: 5, lifeSteps: 90, scale: 0.85, charges: gens },
            );
          }
        }
        if (!survives) {
          pool.despawn(i);
          i--;
        }
      }
    }
  }

  /**
   * Redirect a surviving player shot at the nearest OTHER live enemy within
   * bounce-seek range, preserving its speed. False = nothing to seek.
   */
  private retargetShot(i: number, excludeIdx: number): boolean {
    const pool = this.playerShots;
    const sx = pool.x[i]!;
    const sy = pool.y[i]!;
    let best = -1;
    let bestD = 400 * 400;
    for (let k = 0; k < this.enemies.length; k++) {
      if (k === excludeIdx) continue;
      const e = this.enemies[k]!;
      if (!e.alive) continue;
      const d = dist2(sx, sy, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    if (best < 0) return false;
    const target = this.enemies[best]!;
    const speed = Math.hypot(pool.vx[i]!, pool.vy[i]!) || 1;
    const aim = Math.atan2(target.y - sy, target.x - sx);
    pool.vx[i] = Math.cos(aim) * speed;
    pool.vy[i] = Math.sin(aim) * speed;
    // Step clear of the enemy just hit, or the next collision tick re-hits
    // it in place and the charges burn out without ever traveling.
    const hitE = this.enemies[excludeIdx]!;
    const clear = hitE.radius + pool.radius[i]! + 6;
    pool.x[i] = sx + Math.cos(aim) * clear;
    pool.y[i] = sy + Math.sin(aim) * clear;
    pool.age[i] = 0; // a fresh leg, not a dying one
    return true;
  }

  private collideContact(): void {
    const player = this.player;
    if (player.iframes > 0 || !player.alive) return;
    let contact: Enemy | null = null;
    this.hash.query(player.x, player.y, CONTACT_CHECK_RANGE, (idx) => {
      if (contact) return;
      const e = this.enemies[idx];
      if (!e || !e.alive) return;
      // 0-contact enemies (goblin) never land a hit — damagePlayer(0) would
      // still consume a Wardstone charge, so skip them entirely.
      if (e.contactDamage === 0) return;
      const rr = e.radius * 0.8 + player.hitRadius;
      if (dist2(player.x, player.y, e.x, e.y) <= rr * rr) contact = e;
    });
    if (contact) this.damagePlayer((contact as Enemy).contactDamage);
  }

  /** Player stats are modifier-driven; sync derived fields each tick. */
  private syncPlayerStats(): void {
    const stats = this.runState.stats;
    const player = this.player;
    player.speed = stats.resolve('moveSpeed');
    // Cask's powder burns scorch the ceiling (floor 1 — burns never kill).
    // The raise-heals-delta rule credits every legitimate maxHp gain
    // (mid-floor burn cooling, Iron Heart, +maxHp artifacts). Descent's
    // burn clear is the ONE exception (round 5): burnCreditHold suppresses
    // the credit exactly once — ceiling back, flesh unhealed.
    const mh = Math.max(1, Math.round(stats.resolve('maxHp')) - Math.round(this.runState.burnedHp));
    if (mh !== player.maxHp) {
      if (mh > player.maxHp && !this.runState.burnCreditHold) {
        player.hp += mh - player.maxHp; // raising max heals the delta
      }
      player.maxHp = mh;
      player.hp = Math.min(player.hp, mh);
    }
    // Cleared UNCONDITIONALLY: a zero-burn descent must never leave the
    // hold armed to eat the next artifact's +maxHp credit.
    this.runState.burnCreditHold = false;
  }

  // -------------------------------------------------------------- boss

  private tickSummoning(dt: number, input: InputSnapshot): void {
    // Bar full → the circle forms at its precomputed (seeded) position.
    if (!this.circleSpawned && summonBarFill(this.runState.barFill) >= 1) {
      this.circleSpawned = true;
      const c = this.map.circleCandidates[0] ?? this.map.exit;
      this.circle = { x: (c.x + 0.5) * TILE, y: (c.y + 0.5) * TILE };
      this.events.emit('circleAppeared', this.circle);
    }
    // Deliberate trigger: stand in the circle and hold interact.
    if (this.circle && !this.boss) {
      const near = dist2(this.player.x, this.player.y, this.circle.x, this.circle.y) < 52 * 52;
      if (near && input.interactHeld) {
        this.channel += dt;
        if (this.channel >= 1.5) this.startBossFight();
      } else {
        this.channel = 0;
      }
    }
  }

  private startBossFight(): void {
    const circle = this.circle!;
    this.circle = null;
    this.channel = 0;

    // Arena = the chamber holding the circle; the player is confined to it.
    // Prefer the room whose CENTER is the circle tile (circle candidates are
    // chamber centers) so overlapping rects can't grab the wrong chamber.
    const ctx = Math.floor(circle.x / TILE);
    const cty = Math.floor(circle.y / TILE);
    const room =
      this.map.rooms.find((r) => r.cx === ctx && r.cy === cty) ??
      this.map.rooms.find(
        (r) => ctx >= r.x && ctx < r.x + r.w && cty >= r.y && cty < r.y + r.h,
      );
    if (room) {
      this.arena = {
        minX: (room.x + 0.6) * TILE,
        minY: (room.y + 0.6) * TILE,
        maxX: (room.x + room.w - 0.6) * TILE,
        maxY: (room.y + room.h - 0.6) * TILE,
      };
    }

    // Draw the boss from the pool: a fresh seeded stream keyed on the summon
    // floor, so the same seed+floor always faces the same boss no matter WHEN
    // the player channels — and no other stream is perturbed.
    const bd =
      BOSS_DEFS.find((d) => d.id === this.forceBoss) ??
      this.runState.streams.stream('boss', this.floor).pick(BOSS_DEFS);
    this.bossDef = bd;
    // Per-boss stat profile rides a derived def; gemValue stays shared (the
    // win payout is variant-independent).
    const def = {
      ...ENEMY_DEFS.boss,
      speed: bd.speed, radius: bd.radius, contactDamage: bd.contactDamage,
    };
    const boss = new Enemy(
      this.nextEnemyId++, 'boss', def,
      bd.hpMult * bossHpMult(this.floor) * this.tierHp,
    );
    const bx = this.arena ? (this.arena.minX + this.arena.maxX) / 2 : circle.x;
    const by = this.arena ? (this.arena.minY + this.arena.maxY) / 2 : circle.y;
    boss.place(bx, by);
    this.boss = boss;
    this.bossPhaseIdx = 0;
    this.setBossPhase(0);
    this.bossInvuln = 45;
    this.enemies.push(boss);
    this.events.emit('bossStarted', { name: bd.name });
  }

  private setBossPhase(idx: number): void {
    const phase = this.bossDef?.phases[idx];
    if (!phase || !this.boss) return;
    this.boss.runner = new PhaseRunner(
      this.registry,
      bossTracksForFloor(phase, this.floor),
      this.runState.streams.patternStream(this.floor, 900000 + idx),
      toSteps,
    );
    for (const add of phase.adds) {
      for (let i = 0; i < add.count; i++) {
        const def = ENEMY_DEFS[add.type];
        const e = new Enemy(this.nextEnemyId++, add.type, def, enemyHpMult(this.floor) * this.tierHp);
        const a = this.arena ?? this.bounds;
        e.place(
          gameplayRng.bool() ? a.minX + 40 : a.maxX - 40,
          gameplayRng.bool() ? a.minY + 40 : a.maxY - 40,
        );
        if (def.firePattern) {
          e.runner = new PhaseRunner(
            this.registry,
            [[{ loop: [{ pattern: def.firePattern }, { wait: def.fireCooldown }] }]],
            this.runState.streams.patternStream(this.floor, 910000 + idx * 100 + i),
            toSteps,
          );
        }
        this.enemies.push(e);
      }
    }
  }

  // -------------------------------------------------------------- vault

  /** Dormant until approached: the Keeper wakes when the player nears the arena. */
  private tickKeeper(): void {
    if (this.keeperSpawned || !this.arenaCenter) return;
    const c = this.arenaCenter;
    if (dist2(this.player.x, this.player.y, c.x, c.y) > 350 * 350) return;
    this.keeperSpawned = true;

    const def = ENEMY_DEFS.keeper;
    const keeper = new Enemy(this.nextEnemyId++, 'keeper', def, enemyHpMult(this.floor) * this.tierHp);
    keeper.place(c.x, c.y);
    if (this.keeperDrop && this.keeperDrop.artifactId !== null) {
      keeper.dropArtifactId = this.keeperDrop.artifactId;
      keeper.dropQuality = this.keeperDrop.quality;
      keeper.dropProc = this.keeperDrop.proc;
      keeper.dropCurseId = this.keeperDrop.curseId;
    }
    // Single-phase runner over existing patterns; ordinal 950000 is the
    // keeper's reserved namespace (boss uses 900000/910000, ambush 800000).
    keeper.runner = new PhaseRunner(
      this.registry,
      [
        [{ loop: [{ pattern: 'twinSpiral' }, { wait: 1.8 }] }],
        [{ wait: 1 }, { loop: [{ pattern: 'chanterSlam' }, { wait: 2.4 }] }],
      ],
      this.runState.streams.patternStream(this.floor, 950000),
      toSteps,
    );
    this.keeper = keeper;
    this.enemies.push(keeper);
    this.events.emit('keeperAwakened', { x: c.x, y: c.y });
  }

  /**
   * Cleansing altar: inert while the Keeper lives (or before it wakes); one
   * use; hold-to-channel like the summoning circle. The scene layer picks
   * WHAT to cleanse via CleanseOverlay on `altarChannelComplete` and then
   * sets `altarUsed`.
   */
  /** True if the run holds anything the altar can cleanse. */
  hasCleanseTarget(): boolean {
    return this.runState.artifacts.some((a) => a.proc === 'cursed') ||
      this.runState.shrineCurses.length > 0;
  }

  private tickAltar(dt: number, input: InputSnapshot): void {
    if (!this.altarPos || this.altarUsed || !this.vaultBossDefeated) return;
    if (!this.hasCleanseTarget()) return;
    const near = dist2(this.player.x, this.player.y, this.altarPos.x, this.altarPos.y) < 52 * 52;
    if (near && input.interactHeld) {
      this.altarChannel += dt;
      if (this.altarChannel >= 1.5) {
        this.altarChannel = 0;
        relightCandle(this.runState); // Wick: content interactions refuel
        this.events.emit('altarChannelComplete', { x: this.altarPos.x, y: this.altarPos.y });
      }
    } else {
      this.altarChannel = 0;
    }
  }

  private tickBossPhases(): void {
    const boss = this.boss!;
    const next = this.bossDef?.phases[this.bossPhaseIdx + 1];
    if (next && boss.hp / boss.maxHp <= next.hpBelow) {
      this.bossPhaseIdx++;
      this.enemyBullets.clear(); // breath between phases
      this.bossInvuln = 60;
      this.setBossPhase(this.bossPhaseIdx);
      this.events.emit('bossPhaseChanged', { phase: this.bossPhaseIdx + 1 });
    }
  }

  /** Curse-touched elites radiate a curse that halves healing. */
  private tickCurseAura(): void {
    const p = this.player;
    for (const e of this.enemies) {
      if (e.affix !== 'curseTouched' || !e.alive) continue;
      if (dist2(p.x, p.y, e.x, e.y) < 140 * 140) {
        p.curseLeft = Math.max(p.curseLeft, 8);
      }
    }
  }

  private pickupArtifacts(): void {
    const p = this.player;
    if (!p.alive) return;
    for (let i = this.artifactDrops.length - 1; i >= 0; i--) {
      const drop = this.artifactDrops[i]!;
      const inRange = dist2(p.x, p.y, drop.x, drop.y) < 32 * 32;

      // Suppression (declined swap) clears once the player walks away.
      if (drop.suppressed) {
        if (dist2(p.x, p.y, drop.x, drop.y) > 48 * 48) drop.suppressed = false;
        continue;
      }
      if (!inRange) continue;

      const rs = this.runState;
      const equippedQ = rs.equippedQuality(drop.artifactId);
      if (equippedQ !== undefined) {
        // The quality chase: a better copy of something equipped improves it
        // in place (no slot consumed); equal/worse crumbles.
        this.artifactDrops.splice(i, 1);
        if (drop.quality > equippedQ) {
          this.events.emit('artifactImproved', {
            artifactId: drop.artifactId, from: equippedQ, to: drop.quality,
            proc: drop.proc, curseId: drop.curseId,
          });
        } else {
          this.events.emit('artifactDust', {
            artifactId: drop.artifactId, quality: drop.quality,
          });
        }
      } else if (rs.artifacts.length < rs.artifactCapacity) {
        this.artifactDrops.splice(i, 1);
        this.events.emit('artifactPickedUp', {
          artifactId: drop.artifactId, quality: drop.quality,
          proc: drop.proc, curseId: drop.curseId,
        });
      } else {
        // At capacity: pause for a decision instead of auto-grabbing.
        drop.suppressed = true;
        this.events.emit('artifactSwapPrompt', {
          artifactId: drop.artifactId, quality: drop.quality,
          proc: drop.proc, curseId: drop.curseId,
        });
      }
    }
  }

  /**
   * S1 ground zones: expire, then affect live enemies inside — direct dps
   * ('caustic'/'sanctum'), or a burn DoT ('burn', feeds burn-detonation), and
   * an optional slow (zoneSlow fields, consumed by behaviors next tick).
   */
  private tickZones(dt: number): void {
    for (let z = this.zones.length - 1; z >= 0; z--) {
      const zone = this.zones[z]!;
      zone.duration -= dt;
      if (zone.duration <= 0) {
        this.zones.splice(z, 1);
        continue;
      }
      this.hash.query(zone.x, zone.y, zone.radius + 30, (idx) => {
        const e = this.enemies[idx];
        if (!e || !e.alive) return;
        if (dist2(zone.x, zone.y, e.x, e.y) > (zone.radius + e.radius) ** 2) return;
        if (zone.kind === 'burn') {
          e.burnLeft = Math.max(e.burnLeft, 1.0);
          e.burnDps = Math.max(e.burnDps, zone.dps);
        } else if (zone.dps > 0) {
          this.damageEnemy(idx, zone.dps * dt, 'dot');
        }
        if (zone.slowPct > 0) {
          e.zoneSlowLeft = 0.15;
          e.zoneSlowMult = Math.min(e.zoneSlowMult, 1 - zone.slowPct);
        }
      });
      // 'gas' is hostile to BOTH sides: the player takes a contact hit,
      // naturally paced by i-frames (~1.2s) — "don't stand in it".
      if (zone.kind === 'gas') {
        const pl = this.player;
        if (pl.alive && dist2(zone.x, zone.y, pl.x, pl.y) <= (zone.radius + pl.hitRadius) ** 2) {
          this.damagePlayer(1);
        }
      }
    }
  }

  /**
   * Environmental hazards (ME4) — rng-free ticking: fixed periods, phases
   * staggered at roll time. Both kinds warn via shared telegraph zones
   * (their commit path damages the player, tier-scaled + i-framed); the
   * paired enemy hits + side effects ride the `pending` queue, counted
   * down here to land on the same ticks. Debris fires DEBRIS_STEPS zones
   * marching down its lane, staggered by DEBRIS_STEP_DELAY. Hazard hits
   * use kind 'dot' — environmental damage never stacks Fracture shred.
   * Keeps ticking during boss fights; the vault never has hazards.
   */
  private tickHazards(dt: number): void {
    for (const hz of this.hazards) {
      for (let i = hz.pending.length - 1; i >= 0; i--) {
        const p = hz.pending[i]!;
        p.commitIn -= dt;
        if (p.commitIn < 0) {
          hz.pending.splice(i, 1);
          this.commitHazardStrike(hz, p.x, p.y);
        }
      }
      hz.timer -= dt;
      if (hz.timer <= 0) {
        if (hz.kind === 'vent') {
          hz.timer += VENT_PERIOD;
          this.telegraphs.spawnZone(hz.x, hz.y, VENT_RADIUS, VENT_LEAD_STEPS, VENT_DAMAGE);
          hz.pending.push({ x: hz.x, y: hz.y, commitIn: VENT_LEAD_STEPS / 60 });
        } else {
          // Ceiling collapse: impacts march from one lane end to the other.
          hz.timer += DEBRIS_PERIOD;
          const ux = Math.cos(hz.angle);
          const uy = Math.sin(hz.angle);
          for (let s = 0; s < DEBRIS_STEPS; s++) {
            const t = (s / Math.max(1, DEBRIS_STEPS - 1)) * 2 - 1;
            const x = hz.x + ux * hz.halfLen * t;
            const y = hz.y + uy * hz.halfLen * t;
            const delaySteps = Math.round(s * DEBRIS_STEP_DELAY * 60);
            this.telegraphs.spawnZone(x, y, DEBRIS_RADIUS, DEBRIS_LEAD_STEPS + delaySteps, DEBRIS_DAMAGE, 'debris');
            hz.pending.push({ x, y, commitIn: (DEBRIS_LEAD_STEPS + delaySteps) / 60 });
          }
        }
      }
    }
  }

  /** One hazard impact landing: enemy damage (+ gas for vents). */
  private commitHazardStrike(hz: Hazard, x: number, y: number): void {
    const radius = hz.kind === 'vent' ? VENT_RADIUS : DEBRIS_RADIUS;
    const damage = hz.kind === 'vent' ? VENT_ENEMY_DAMAGE : DEBRIS_ENEMY_DAMAGE;
    // The telegraph commit already hit the player; enemies here.
    this.hash.query(x, y, radius + 30, (idx) => {
      const e = this.enemies[idx];
      if (!e || !e.alive) return;
      if (dist2(x, y, e.x, e.y) <= (radius + e.radius) ** 2) {
        this.damageEnemy(idx, damage, 'dot');
      }
    });
    if (hz.kind === 'vent') {
      this.zones.push({
        x, y, radius: VENT_RADIUS, duration: GAS_DURATION,
        dps: GAS_DPS, slowPct: 0, kind: 'gas',
      });
    } else {
      // Rubble lands — the renderer draws a brown impact ring (ME4 rework).
      this.events.emit('debrisImpact', { x, y, radius: DEBRIS_RADIUS });
    }
    this.spawnSparks(x, y, hz.kind === 'vent' ? 6 : 8, hz.kind === 'vent' ? 1.0 : 1.3);
  }

  /** S2 decay: brand/stun/burn/zone-slow timers + the burn DoT itself. */
  private tickDebuffs(dt: number): void {
    for (const e of this.enemies) {
      if (!e.alive) continue;
      if (e.brandLeft > 0) e.brandLeft -= dt;
      if (e.stunLeft > 0) e.stunLeft -= dt;
      if (e.zoneSlowLeft > 0) {
        e.zoneSlowLeft -= dt;
        if (e.zoneSlowLeft <= 0) e.zoneSlowMult = 1;
      }
      if (e.burnLeft > 0) {
        if (e.type === 'boss' && this.bossInvuln > 0) continue;
        e.burnLeft -= dt;
        e.hp -= e.burnDps * dt;
        if (e.hp <= 0) this.killEnemy(e);
      }
    }
  }

  private tickWither(dt: number): void {
    for (const e of this.enemies) {
      if (!e.alive || e.witherLeft <= 0) continue;
      if (e.type === 'boss' && this.bossInvuln > 0) continue;
      e.witherLeft -= dt;
      e.hp -= e.witherDps * dt;
      if (e.hp <= 0) this.killEnemy(e);
    }
  }

  // -------------------------------------------------------------- gems

  private spawnGems(x: number, y: number, totalValue: number): void {
    // Split into a few gems for the shower feel; each carries value in p0.
    let remaining = totalValue;
    const parts = Math.min(4, Math.max(1, Math.round(totalValue / 4)));
    for (let i = 0; i < parts; i++) {
      const v = i === parts - 1 ? remaining : Math.max(1, Math.round(totalValue / parts));
      remaining -= v;
      const gi = this.gems.spawn(
        x, y, gameplayRng.range(0, TAU), gameplayRng.range(40, 140),
        KIND_GEM, 0, 7200, 0.55 + Math.min(0.5, v * 0.03),
      );
      if (gi < 0) {
        this.orphanGemValue += v;
      } else {
        // Decelerate to rest; the gem's VALUE rides in p1 (p0/p2 belong to
        // the Accel mod — storing value in p0 was a real bug once).
        this.gems.setMod(gi, 1, -220, v, 0);
      }
    }
  }

  /**
   * Pickup-track stat bumps: a pure function of gemsEarned × pickupRateMult —
   * order-independent, no pause. Source 'pickups' is run-permanent (never
   * floorBuffs). Runs in the vault too (shared runState). The WHICH-stat
   * table stays zero-rng; the bonus mend roll is gameplayRng (reactive
   * class — collection timing is live-play anyway, never a seeded stream).
   */
  private tickPickupBumps(): void {
    const rs = this.runState;
    const effective = rs.gemsEarned * rs.stats.resolve('pickupRateMult');
    const earned = Math.min(PICKUP_BUMP_CAP, pickupBumpsEarned(effective));
    while (rs.pickupBumpsGranted < earned) {
      const k = rs.pickupBumpsGranted++;
      const { label, ...mod } = PICKUP_BUMP_TABLE[k % PICKUP_BUMP_TABLE.length]!;
      rs.stats.addModifiers('pickups', [mod]);
      this.events.emit('statBump', { index: k, label });
      // Small chance the gems mend flesh too (respects heal blocks: Vane
      // never benefits, Thirl's Ebb ignores it — aspect identity holds).
      if (gameplayRng.next() < PICKUP_HEAL_CHANCE && this.player.heal(1) > 0) {
        this.events.emit('pickupHealed', { amount: 1 });
      }
    }
  }

  private updateGems(dt: number): void {
    const player = this.player;
    const pool = this.gems;
    pool.update(dt, player.x, player.y, this.bounds);
    if (!player.alive) return;
    // Sunder (Trine): split form cannot pick anything up — the burst window
    // costs collection.
    if (this.sunderLeft > 0) return;
    const hound = this.hound?.alive ? this.hound : null;
    for (let i = 0; i < pool.count; i++) {
      const d = dist2(pool.x[i]!, pool.y[i]!, player.x, player.y);
      const magnet = this.runState.stats.resolve('magnetRange');
      const hd = hound ? dist2(pool.x[i]!, pool.y[i]!, hound.x, hound.y) : Infinity;
      if (d < GEM_COLLECT_RANGE * GEM_COLLECT_RANGE || hd < 34 * 34) {
        // Gaunt vacuums at the hound's mouth; the credit is shared.
        const value = (pool.p1[i]! + this.orphanGemValue) * this.runState.stats.resolve('gemGainMult');
        this.orphanGemValue = 0;
        this.runState.collectGems(value);
        this.events.emit('gemCollected', { value });
        pool.despawn(i);
        i--;
      } else if (hound && hd < HOUND_VACUUM_RANGE * HOUND_VACUUM_RANGE) {
        // Shared Fate: Gaunt's vacuum out-ranges any magnet by far.
        const ang = Math.atan2(hound.y - pool.y[i]!, hound.x - pool.x[i]!);
        pool.modKind[i] = 0;
        pool.vx[i] = Math.cos(ang) * 460;
        pool.vy[i] = Math.sin(ang) * 460;
      } else if (d < magnet * magnet) {
        const ang = Math.atan2(player.y - pool.y[i]!, player.x - pool.x[i]!);
        pool.modKind[i] = 0;
        pool.vx[i] = Math.cos(ang) * 400;
        pool.vy[i] = Math.sin(ang) * 400;
      }
    }
  }

  spawnSparks(x: number, y: number, count: number, scale: number): void {
    for (let i = 0; i < count; i++) {
      this.particles.spawn(
        x, y, gameplayRng.range(0, TAU), gameplayRng.range(60, 260),
        KIND_SPARK, 0, gameplayRng.intRange(18, 40), scale * gameplayRng.range(0.4, 0.9),
      );
    }
  }
}

function TileMap_toWorld(t: number): number {
  return (t + 0.5) * TILE;
}
